import React, { useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { Checkbox, Table } from 'antd'
import type { TableProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { HiCheck } from 'react-icons/hi'
import { ShipdayConnection } from './types'
import { DeleteButton, OutlineButton } from './styles'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'
import { useTableSelection } from '../useTableSelection'

interface ConnectionsPageProps {
  connections: ShipdayConnection[]
  onConnectAccount: () => void
  onConnectStore: () => void
  onDeleteMultiple: (ids: string[]) => void
  onManage: (id: string) => void
}

const ConnectionsPage = ({
  connections,
  onConnectAccount,
  onConnectStore,
  onDeleteMultiple,
  onManage
}: ConnectionsPageProps) => {
  const { merchantLocations } = useMerchant()
  const allIds = useMemo(() => connections.map((c) => c.id), [connections])
  const {
    selectedIds,
    isSelected,
    toggleRow,
    toggleAll,
    isAllSelected,
    hasSelection,
    clearSelection
  } = useTableSelection(allIds)

  const hasConnections = connections.length > 0
  const isMappingComplete =
    hasConnections && connections.every((c) => c.integrationType === 'merchant')

  const storeLabelMap = useMemo(() => {
    const locationById = merchantLocations.reduce<Record<string, string>>(
      (acc, l) => {
        acc[l.id] = l.name
        return acc
      },
      {}
    )
    return connections.reduce<Record<string, string>>((acc, conn) => {
      if (conn.integrationType === 'merchant') {
        acc[conn.id] = 'All current and future stores are connected'
      } else {
        acc[conn.id] = conn.connectedStoreIds
          .map((id) => locationById[id] || id)
          .join(', ')
      }
      return acc
    }, {})
  }, [connections, merchantLocations])

  const getStoreLabel = useCallback(
    (conn: ShipdayConnection) => storeLabelMap[conn.id] ?? '',
    [storeLabelMap]
  )

  const handleBulkDelete = () => {
    onDeleteMultiple(selectedIds)
    clearSelection()
  }

  const COLUMNS: ColumnsType<ShipdayConnection> = useMemo(
    () => [
      {
        title: <StyledCheckbox checked={isAllSelected} onChange={toggleAll} />,
        dataIndex: 'id',
        key: 'checkbox',
        width: 32,
        render: (id: string) => (
          <StyledCheckbox
            checked={isSelected(id)}
            onChange={() => toggleRow(id)}
          />
        )
      },
      {
        title: 'Store Mapping',
        key: 'storeMapping',
        width: '25%',
        render: (_, conn: ShipdayConnection) => (
          <Cell>
            {conn.integrationType === 'merchant'
              ? 'Merchant-level'
              : 'Store-level'}{' '}
            Account — API Key ending ****{conn.token.slice(-4)}
          </Cell>
        )
      },
      {
        title: 'Connected Stores',
        key: 'connectedStores',
        render: (_, conn: ShipdayConnection) => (
          <Cell>{getStoreLabel(conn)}</Cell>
        )
      },
      {
        dataIndex: 'id',
        key: 'actions',
        width: 100,
        render: (id: string, conn: ShipdayConnection) =>
          conn.integrationType === 'location' ? (
            <ManageButton onClick={() => onManage(id)}>MANAGE</ManageButton>
          ) : null
      }
    ],
    [isAllSelected, isSelected, toggleAll, toggleRow, onManage, getStoreLabel]
  )

  return (
    <Root>
      <SectionCard>
        <SectionHeader>
          <StepIndicator completed={hasConnections}>
            {hasConnections ? <HiCheck /> : '1'}
          </StepIndicator>
          <SectionInfo>
            <SectionTitle>Integration Settings</SectionTitle>
            <SectionDescription>
              {hasConnections
                ? 'Your integration is set up and ready.'
                : 'Manage your Shipday account connections and store assignments'}
            </SectionDescription>
          </SectionInfo>
          {hasConnections && (
            <ConnectionCount>
              <ConnectionCountLabel>Total Connections</ConnectionCountLabel>
              <ConnectionCountValue data-testid='connection-count'>
                {connections.length}
              </ConnectionCountValue>
            </ConnectionCount>
          )}
        </SectionHeader>

        {!hasConnections && (
          <OutlineButton style={{ width: '100%' }} onClick={onConnectAccount}>
            CONNECT SHIPDAY ACCOUNT
          </OutlineButton>
        )}
      </SectionCard>

      {hasConnections && (
        <SectionCard>
          <SectionHeader>
            <StepIndicator completed={isMappingComplete}>
              {isMappingComplete ? <HiCheck /> : '2'}
            </StepIndicator>
            <SectionInfo>
              <SectionTitle>Integration mapping</SectionTitle>
              <SectionDescription>
                Manage your Shipday account connections and store assignments
              </SectionDescription>
            </SectionInfo>
          </SectionHeader>

          {isMappingComplete ? (
            connections.map((conn) => (
              <MerchantRow key={conn.id}>
                <Cell>
                  Merchant-level Account — API Key ending ****
                  {conn.token.slice(-4)}
                </Cell>
                <Cell style={{ flex: 1 }}>{getStoreLabel(conn)}</Cell>
                <MerchantRowActions>
                  <DeleteButton onClick={() => onDeleteMultiple([conn.id])}>
                    DELETE
                  </DeleteButton>
                  <ManageButton onClick={() => onManage(conn.id)}>
                    MANAGE
                  </ManageButton>
                </MerchantRowActions>
              </MerchantRow>
            ))
          ) : (
            <>
              <BulkActionBar>
                <BulkActionInfo>
                  <BulkActionTitle>Bulk Actions</BulkActionTitle>
                  <BulkActionSubtitle>
                    Select one or more rows, then choose a bulk action.
                  </BulkActionSubtitle>
                </BulkActionInfo>
                <DeleteButton
                  disabled={!hasSelection}
                  onClick={handleBulkDelete}
                >
                  DELETE CONNECTION(S)
                </DeleteButton>
              </BulkActionBar>

              <StyledTable
                dataSource={connections}
                columns={COLUMNS as TableProps<object>['columns']}
                pagination={false}
                rowKey='id'
              />
            </>
          )}

          {!isMappingComplete && (
            <OutlineButton style={{ width: '100%' }} onClick={onConnectStore}>
              CONNECT A STORE
            </OutlineButton>
          )}
        </SectionCard>
      )}
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const SectionCard = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const StepIndicator = styled.div<{ completed: boolean }>(
  ({ completed }) => `
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;

  ${
    completed
      ? `
    background-color: #d092dd;
    border: 2px solid #d092dd;
    color: #ffffff;

    svg {
      fill: #ffffff;
    }
  `
      : `
    border: 2px solid #d092dd;
    color: #d092dd;
    box-shadow: 0px 0px 0px 4px #d092dd33;
  `
  }
`
)

const SectionInfo = styled.div`
  flex: 1;
`

const SectionTitle = styled.strong`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #2b2b50;
`

const SectionDescription = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #77778e;
`

const ConnectionCount = styled.div`
  text-align: right;
`

const ConnectionCountLabel = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
  margin-bottom: 0;
`

const ConnectionCountValue = styled.p`
  font-size: 20px;
  font-weight: 700;
  color: #2b2b50;
  margin-bottom: 0;
`

const BulkActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #d2d2d9;
  border-radius: 4px;
  padding: 8px 12px;
`

const BulkActionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const BulkActionTitle = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #4a4a68;
`

const BulkActionSubtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
`

const StyledCheckbox = styled(Checkbox)`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -12px;
    left: -12px;
    width: 48px;
    height: 48px;
    z-index: 1;
  }
`

const Cell = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #4a4a68;
  line-height: 1.2;
`

const StyledTable = styled(Table)`
  .ant-table-tbody > tr {
    &:hover {
      background-color: #fafafb;
    }
  }
  border: 1px solid #dbd9df;
  border-radius: 6px;
  overflow: hidden;

  .ant-table-row {
    > .ant-table-cell {
      border-bottom: 1px solid #dbd9df;
      padding: 12px;
      line-height: 1;
    }

    &:last-of-type {
      > .ant-table-cell {
        border-bottom: none;
      }
    }
  }

  thead > tr {
    > .ant-table-cell {
      background: #f3f2f4;
      border-bottom: 1px solid #dbd9df;
      padding: 12px;
      line-height: 1;
    }
  }

  .ant-table-cell {
    position: relative;

    &:not(:first-of-type):not(:nth-last-of-type(-n + 2)) {
      &::after {
        content: '';
        position: absolute;
        top: 12px;
        bottom: 12px;
        right: 0;
        width: 1px;
        background: #d2d2d9;
      }
    }
  }
` as unknown as typeof Table

const MerchantRow = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #dbd9df;
  border-radius: 6px;
  padding: 12px;

  > span:first-of-type {
    border-right: 1px solid #d2d2d9;
    padding-right: 12px;
    margin-right: 12px;
  }

  > span + span {
    margin-right: 12px;
  }
`

const MerchantRowActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`

const ManageButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #4a4a68;
  border-radius: 4px;
  background: transparent;
  color: #4a4a68;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.04em;

  &:hover:not(:disabled) {
    background: #f3f2f4;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export default ConnectionsPage
